import { AgGridAngular } from "@ag-grid-community/angular";
import {
  ColDef,
  GetContextMenuItemsParams,
  GetGroupRowAggParams,
  GridOptions,
  HeaderValueGetterFunc,
  HeaderValueGetterParams,
  ICellRendererParams,
  IRowNode,
  MenuItemDef,
  ValueFormatterParams,
  ValueGetterParams,
} from "@ag-grid-community/core";
import { AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject, OnInit, signal, ViewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import {
  AccountMarketplace,
  Currency,
  KeywordTrackerConfig,
  KeywordTrackingFrequency,
  Marketplace,
  ProductTrackerConfig,
  QueryType,
  Targeting,
} from "@front/m19-api-client";
import { getGroupRowAgg, gridConfig } from "@front/m19-grid-config";
import { ACOS, AD_CONVERSIONS, AD_SALES, CLICKS, CONVERSION_RATE, COST, CPC, Metric, ROAS } from "@front/m19-metrics";
import { AdStatsEx, Catalog, ProductEx, SegmentConfigType, SegmentEx } from "@front/m19-models";
import {
  AccountMarketplaceService,
  AccountSelectionService,
  AdsStatsWithPreviousPeriod,
  AmazonSearchUrlPipe,
  AsinService,
  AuthService,
  CurrencyService,
  KeywordTrackingService,
  SegmentService,
  StatsService,
  TruncatePipe,
  UserSelectionService,
} from "@front/m19-services";
import {
  ActionButton,
  ActionButtonsComponent,
  ConfirmPopupComponent,
  IButtonDirective,
  IIconComponent,
  ITooltipDirective,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { Comparison, DateUtils, MetricTitlePipe, sumAdStats, Utils } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ChartLayoutComponent } from "@m19-board/containers/chart-layout/chart-layout.component";
import { GridLayoutComponent } from "@m19-board/containers/grid-layout/grid-layout.component";
import { GridConfigService } from "@m19-board/grid-config/grid-config.service";
import { MetricSelectorComponent } from "@m19-board/insights/metric-selector/metric-selector.component";
import { KeywordSegmentModalComponent } from "@m19-board/segments/keyword-segment-modal/keyword-segment-modal.component";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { TopQueriesListComponent } from "@m19-board/traffic-stats/top-queries-list.component";
import { TrafficStatsChartComponent } from "@m19-board/traffic-stats/traffic-stats-chart.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { MetricsSelectorPage, MetricsSelectorKey } from "libs/m19-services/src/lib";
import { BehaviorSubject, combineLatest, defer, Observable, of } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { AddToSeedsModalComponent, AddToSeedsModalData, SeedType } from "../catalog/seeds/add-to-seeds-modal.component";
import { actionColumnProperties, CurrencyColumn } from "../grid-config/grid-columns";
import { ACTIONS_COL_ID, exportGridCsv, getCsvFileName, IMAGE_COL_ID, STATUS_BAR } from "../grid-config/grid-config";
import { QueryStats } from "../models/Metric";
import { ProductTrackerPageQueryParams } from "../product-tracker/product-tracker-views/product-tracker.component";
import { AsinLinkComponent } from "../product-view/asin-link.component";
import { ProductViewComponent } from "../product-view/product-view.component";
import { AddToSegmentModalComponent, AddToSegmentModalData } from "../segments/add-to-segment-modal.component";
import { ProductSegmentModalComponent, ProductSegmentModalData } from "../segments/product-segment-modal.component";
import { keywordRankingAvailableFor } from "../tracking/KeywordRankingAvailability";
import {
  DefaultTrafficStatsFilter,
  TrafficStatsFilter,
  TrafficStatsFilterComponent,
} from "./traffic-stats-filter.component";

type QueryStatsWithPreviousData = {
  data: QueryStats[];
  previousData: QueryStats[];
};

export type QueryStatsExtraction = {
  data: QueryStats[];
  allData: QueryStats[];
};

function buildFakeData(date?: string) {
  const isAsin = Utils.randomBoolean();
  return {
    impressions: Utils.randomInt(30000),
    clicks: Utils.randomInt(200),
    cost: Utils.randomInt(3000) / 100,
    adConversions: Utils.randomInt(20),
    adSales: Utils.randomInt(200),
    allOrderedUnits: Utils.randomInt(50),
    allSales: Utils.randomInt(10000) / 100,
    date: date,
    isAsin: isAsin,
    asin: "B00KNOUYY0",
    query: isAsin ? "B07MMFRH13" : "fake data",
    queryType: isAsin ? QueryType.PRODUCT : QueryType.KEYWORDS,
    currency: Currency.EUR,
    isMultiAsin: false,
    isUnknownAsin: false,
  };
}

function buildFakeDataBetweenDates(start: string, end: string): QueryStats[] {
  const result: QueryStats[] = [];
  for (let dt = new Date(start); dt <= new Date(end); dt.setDate(dt.getDate() + 1)) {
    result.push(buildFakeData(DateUtils.formatDateForApi(dt)));
  }
  return result;
}

const LastWeek = DateUtils.formatDateForApiFromToday(-7);
const Today = DateUtils.formatDateForApiFromToday(0);
export const FakeData = buildFakeDataBetweenDates(LastWeek, Today);

@UntilDestroy()
@Component({
  selector: "app-traffic-stats",
  templateUrl: "./traffic-stats.component.html",
  styleUrls: ["./traffic-stats.component.scss"],
  imports: [
    TranslocoDirective,
    TrafficStatsFilterComponent,
    ExportButtonComponent,
    IButtonDirective,
    IIconComponent,
    ITooltipDirective,
    MetricSelectorComponent,
    TopQueriesListComponent,
    TrafficStatsChartComponent,
    AsyncPipe,
    AgGridAngular,
    GridLayoutComponent,
    ChartLayoutComponent,
    MetricTitlePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrafficStatsComponent implements OnInit {
  private statsService = inject(StatsService);
  private accountSelection = inject(AccountSelectionService);
  private userSelectionService = inject(UserSelectionService);
  private authService = inject(AuthService);
  private segmentService = inject(SegmentService);
  private accountMarketplaceService = inject(AccountMarketplaceService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private modalService = inject(ModalService);
  private keywordTrackingService = inject(KeywordTrackingService);
  private asinService = inject(AsinService);
  private toasterService = inject(ToasterService);
  private translocoService = inject(TranslocoService);

  private readonly gridConfigService = inject(GridConfigService);
  private readonly currencyService = inject(CurrencyService);

  readonly ChartMetrics: Metric<AdStatsEx>[] = [
    AD_SALES,
    AD_CONVERSIONS,
    COST,
    ACOS,
    CLICKS,
    CONVERSION_RATE,
    ROAS,
    CPC,
  ];
  selectedMetrics: Metric<QueryStats>[] = [];
  CurrencyColumn: ColDef = {
    ...CurrencyColumn,
    headerName: this.translocoService.translate("common.currency", {}, "en"),
    headerValueGetter: () => this.translocoService.translate("common.currency"),
  };

  accountMarketplace = signal<AccountMarketplace | undefined>(undefined);
  marketplace: Marketplace = Marketplace.FR;
  loader$: BehaviorSubject<boolean>;
  topKwLoadingStatus = signal(false);
  topProductLoadingStatus = signal(false);
  currency = toSignal(this.userSelectionService.selectedCurrency$, { initialValue: Currency.EUR });
  locale = toSignal(this.authService.loggedUser$.pipe(map((user) => user.locale)), { initialValue: "fr" });
  aggregatedData = signal<QueryStats | undefined>(undefined);
  previousAggregatedData = signal<QueryStats | undefined>(undefined);
  mainMetric = signal<Metric<QueryStats>>(AD_SALES);
  topKeywords = signal<QueryStats[]>([]);
  topProducts = signal<QueryStats[]>([]);
  chartDisplayed = true;
  keywordTrackingConfig?: KeywordTrackerConfig[];
  productTrackingConfig?: ProductTrackerConfig[];
  catalog?: Catalog;
  isReadOnly = false;
  readonly localStorageKey: MetricsSelectorKey = { page: MetricsSelectorPage.traffic };

  amazonSearchUrlPipe = new AmazonSearchUrlPipe();

  public trafficStatsFilter$ = new BehaviorSubject<TrafficStatsFilter>(DefaultTrafficStatsFilter);
  public initSegment = signal<SegmentEx | undefined>(undefined);
  private selectedMetric$!: BehaviorSubject<Metric<QueryStats>[]>;
  private queryStatsData$ = new BehaviorSubject<QueryStatsWithPreviousData>({
    data: [],
    previousData: [],
  });
  queryStats = signal<QueryStatsExtraction | undefined>(undefined);

  // AG grid config
  gridData = signal<QueryStats[] | undefined>(undefined);
  previousGridData = new Map<string, QueryStats>();
  @ViewChild(AgGridAngular) grid!: AgGridAngular;
  private GRID_KEY = "trafficGrid";

  readonly gridComponents = {
    productView: ProductViewComponent,

    productSearchUrl: (params: ICellRendererParams) => {
      const link = this.amazonSearchUrlPipe.transform(params.value, this.marketplace);
      return `<a target="_blank" class="text-color" href="${link}">${params.data.query}</a>`;
    },
  };

  // Col defs common to all Columns
  public defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
  };

  public columnDefs: ColDef[] = [
    {
      colId: "queryViewCol",
      field: "query",
      enableRowGroup: true,
      headerName: this.translocoService.translate("traffic-stats.query", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("traffic-stats.query"),
      floatingFilter: true,
      filter: "agTextColumnFilter",
      maxWidth: 300,
      pinned: "left",
      cellRendererSelector: (params) => {
        if (params.node.isRowPinned()) return undefined;
        const data = params.data;
        if (data) {
          if (data.isAsin)
            return {
              component: "productView",
              params: { asin: params.value, marketplace: this.marketplace },
            };
          else return { component: "productSearchUrl", params: params };
        }
        return undefined;
      },
    },
    {
      // for CSV export
      colId: "queryTitle",
      field: "query",
      headerName: this.translocoService.translate("traffic-stats.targeted_product_title", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("traffic-stats.targeted_product"),
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      field: "queryType",
      headerName: this.translocoService.translate("traffic-stats.targeting_type", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("traffic-stats.targeting_type"),
      enableRowGroup: true,
      pinned: "left",
      floatingFilter: true,
      valueFormatter: (params: ValueFormatterParams<QueryType, QueryType>) => {
        return params.value ? this.translocoService.translate(`query-type.${params.value}`) : "";
      },
      filter: "agSetColumnFilter",
      filterValueGetter: (params: ValueGetterParams<AdStatsEx>) =>
        this.translocoService.translate(`query-type.${params.data!.queryType!}`),
    },
    {
      colId: "asin",
      field: "asin",
      headerName: this.translocoService.translate("traffic-stats.advertised_product", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("traffic-stats.advertised_product"),
      enableRowGroup: true,
      floatingFilter: true,
      filter: "agTextColumnFilter",
      pinned: "left",
      cellRendererSelector: (params) => {
        if (params.node.group && params.node.field !== "asin") return undefined;
        if (params.node.isRowPinned()) return undefined;
        else if ((params.node.group ? params.node.key : params.value) == "Multi-ASIN") {
          return undefined;
        } else
          return {
            component: "productView",
            params: { asin: params.node.group ? params.node.key : params.value, marketplace: this.marketplace },
          };
      },
    },
    { colId: "asinTitle", field: "asin", headerName: "ASIN Title", hide: true, suppressColumnsToolPanel: true },
    {
      headerName: this.translocoService.translate("product360.parent_asin", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("product360.parent_asin"),
      floatingFilter: true,
      filter: "agTextColumnFilter",
      pinned: "left",
      enableRowGroup: true,
      hide: true,
      valueGetter: (params: ValueGetterParams) => {
        const key = params.data?.asin || params.node?.key;
        return this.catalog?.getParentAsin(key);
      },
      cellRendererSelector: (params: ICellRendererParams) => {
        return params.node.rowPinned || params.node.group
          ? undefined
          : {
              component: AsinLinkComponent,
              params: {
                asin: params.value || params.node.key,
                marketplace: this.marketplace,
              },
            };
      },
    },
    // Metrics columns
    ...this.gridConfigService.getMetricsColDef(this.ChartMetrics).map(({ headerName, ...def }: ColDef) => ({
      ...def,
      cellRendererParams: (params: ICellRendererParams) => {
        const key = params.data ? `${params.data.query}_${params.data.asin}` : "";
        return {
          ...def.cellRendererParams(params),
          previousData: this.previousGridData.get(key),
          currency: this.currency(),
          locale: this.locale(),
        };
      },
    })),
    this.CurrencyColumn,
    {
      ...actionColumnProperties(),
      headerName: this.translocoService.translate("common.currency", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("common.currency"),
      cellRendererSelector: (params) => {
        if (params.node.isRowPinned() || params.node.group) return undefined;
        return {
          component: ActionButtonsComponent,
          params: {
            actionButtons: [
              {
                icon: "ellipsisVertical",
                tooltip: "Row actions",
                subItems: this.getSubItemsButtons(params.node),
              },
            ] as ActionButton[],
          },
        };
      },
    },
  ];

  autoGroupColumnDef: ColDef = {
    pinned: "left",
  };

  public gridOptions: GridOptions = {
    ...gridConfig({
      gridKey: this.GRID_KEY,
      withFooterRow: true,
    }),
    context: { componentParent: this },
    components: this.gridComponents,
    defaultColDef: this.defaultColDef,
    rowGroupPanelShow: "always",
    columnDefs: this.columnDefs,
    groupDisplayType: "multipleColumns",
    autoGroupColumnDef: this.autoGroupColumnDef,
    showOpenedGroup: false,
    getContextMenuItems: (params) => this.getContextMenuItems(params),
  };

  constructor() {
    this.loader$ = new BehaviorSubject(true);
  }

  ngOnInit(): void {
    this.userSelectionService.togglePeriodComparison(undefined, Comparison.None);
    this.selectedMetric$ = new BehaviorSubject<Metric<QueryStats>[]>(
      this.selectedMetrics.length ? this.selectedMetrics : [AD_SALES, COST],
    );

    this.chartDisplayed = this.userSelectionService.getUserChartDisplayedPreference(this.localStorageKey);
    this.loader$.next(true);
    this.userSelectionService.dateRange$
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.loader$.next(true);
        }),
      )
      .subscribe((_) => {
        this.resetQueryStatsData();
      });
    this.accountSelection.accountGroupSelection$
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.loader$.next(true);
        }),
      )
      .subscribe(() => {
        this.resetQueryStatsData();
      });
    this.accountSelection.readOnlyMode$.pipe(untilDestroyed(this)).subscribe((b) => (this.isReadOnly = b));

    this.accountSelection.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        switchMap((am) =>
          this.keywordTrackingService.getKeywordTrackerConfig(am.accountId, am.marketplace, am.resourceOrganizationId!),
        ),
      )
      .subscribe((s) => (this.keywordTrackingConfig = s));
    this.accountSelection.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        switchMap((am) => this.keywordTrackingService.getProductTrackerConfig(am.accountId, am.marketplace)),
      )
      .subscribe((s) => (this.productTrackingConfig = s));
    this.accountSelection.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        switchMap((am) => this.asinService.getCatalog(am.accountId, am.marketplace)),
      )
      .subscribe((c) => {
        this.catalog = c;
      });

    combineLatest([
      this.route.queryParams,
      this.accountSelection.singleAccountMarketplaceSelection$.pipe(
        untilDestroyed(this),
        tap((am: AccountMarketplace) => {
          this.accountMarketplace.set(am);
          this.marketplace = am.marketplace;
          this.grid?.api.showLoadingOverlay();
        }),
        switchMap((am) => this.segmentService.getSegments(am.accountId, am.marketplace)),
      ),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([queryParams, segmentIndex]) => {
        const segmentId = +queryParams["selectedSegment"];
        if (segmentId && segmentIndex.has(segmentId)) {
          const segment = segmentIndex.get(segmentId);
          this.initSegment.set(segment);
          if (segment?.segmentType === SegmentConfigType.KeywordSegment) {
            this.trafficStatsFilter$.next({
              ...this.trafficStatsFilter$.value,
              keywordSegments: [{ filterValue: [segment], exclude: false }],
            });
          } else {
            this.trafficStatsFilter$.next({
              ...this.trafficStatsFilter$.value,
              productSegments: [{ filterValue: [segment!], exclude: false }],
            });
          }
        }
      });

    combineLatest([
      this.accountSelection.singleAccountMarketplaceSelection$.pipe(
        switchMap((am) =>
          combineLatest([
            this.userSelectionService.dateRange$,
            this.userSelectionService.periodComparison$,
            this.userSelectionService.selectedCurrency$,
          ]).pipe(
            tap((_) => {
              this.loader$.next(true);
            }),
            switchMap(([[minDate, maxDate], previousPeriod, currency]) =>
              combineLatest([
                this.statsService.getQueryStats(am.accountId, am.marketplace, currency, minDate, maxDate),
                previousPeriod && previousPeriod.period && previousPeriod.period.length > 0
                  ? this.statsService.getQueryStats(
                      am.accountId,
                      am.marketplace,
                      currency,
                      previousPeriod.period[0],
                      previousPeriod.period[1],
                    )
                  : of([]),
              ]),
            ),
            map(([data, previousData]) => ({ data, previousData })),
          ),
        ),
      ),
      this.trafficStatsFilter$,
    ])
      .pipe(
        tap(() => {
          this.loader$.next(true);
        }),
        map(([{ data, previousData }, filter]) => {
          this.resetQueryStatsData();
          return { data, previousData, filter };
        }),
        untilDestroyed(this),
      )
      .subscribe(({ data, previousData, filter }) => {
        this.initQueryStatsData({ data: data ?? [], previousPeriodData: previousData ?? [] }, filter);
      });

    this.queryStatsData$
      .pipe(
        tap((_) => this.grid?.api.showLoadingOverlay()),
        untilDestroyed(this),
      )
      .subscribe(({ data, previousData }) => this.initAggregatedData(data, previousData));
    this.selectedMetric$.pipe(untilDestroyed(this)).subscribe((m) => this.mainMetric.set(m[0]));
    combineLatest([this.selectedMetric$, this.queryStatsData$])
      .pipe(untilDestroyed(this))
      .subscribe(([m, data]) => this.updateGraphs(m, data.data));

    this.queryStatsData$.pipe(untilDestroyed(this)).subscribe((d) => this.updateTableData(d.data, d.previousData));
    // display fake data if no account marketplace available
    this.accountMarketplaceService.accountMarketplaces$.pipe(untilDestroyed(this)).subscribe((accountMarketplaces) => {
      const noAccountGroupSetup =
        accountMarketplaces?.length == 0 || accountMarketplaces!.findIndex((am) => am.accountGroupId! > 0) < 0;
      if (noAccountGroupSetup) {
        this.initQueryStatsData({ data: FakeData, previousPeriodData: [] }, DefaultTrafficStatsFilter);
      }
    });
  }

  // Function given to ag grid to aggregate Adstats on grouping
  getGroupRowAgg(params: GetGroupRowAggParams) {
    return getGroupRowAgg(params);
  }

  updateTrafficStatsFilter(filter: TrafficStatsFilter): void {
    this.trafficStatsFilter$.next(filter);
  }

  selectMetric(metrics: Metric<QueryStats>[]) {
    this.selectedMetric$.next(metrics);
  }

  updateGraphs(m: Metric<QueryStats>[], data: QueryStats[]): void {
    const mainMetric = m[0];
    const kwData = data.filter((d) => d.queryType === QueryType.KEYWORDS);
    const productData = data.filter((d) => d.queryType === QueryType.PRODUCT);
    this.topKwLoadingStatus.set(true);
    this.topProductLoadingStatus.set(true);
    this.getTopQueries(kwData, mainMetric)
      .pipe(untilDestroyed(this))
      .subscribe((top) => {
        this.topKwLoadingStatus.set(false);
        this.topKeywords.set(top);
      });
    this.getTopQueries(productData, mainMetric)
      .pipe(untilDestroyed(this))
      .subscribe((top) => {
        this.topProductLoadingStatus.set(false);
        this.topProducts.set(top);
      });
  }

  sumData(data: QueryStats[]): QueryStats {
    const tempData = data.slice();
    return tempData.reduce((previous, current) => ({ ...current, ...sumAdStats(previous, current) }), {} as QueryStats);
  }

  getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    const result: (string | MenuItemDef)[] = [
      "copy",
      "chartRange",
      {
        name: "Export CSV",
        action: () => {
          this.exportGridCsv();
        },
        icon: "csv",
      },
    ];

    if (params.node?.group) return result;

    result.push(
      "separator",
      ...this.getSubItemsButtons(params.node!)!.map((actionBtn: ActionButton) => ({
        name: actionBtn.title!,
        action: () => actionBtn.onClick!(params),
      })),
    );

    return result;
  };

  private getSubItemsButtons(node: IRowNode): ActionButton[] | undefined {
    const data = node.data;
    if (!data) return undefined;

    const result: ActionButton[] = [];

    if (!this.isReadOnly) {
      if (data.queryType === QueryType.KEYWORDS) {
        result.push({
          title: "Add '" + data.query + "' to a segment",
          onClick: () => {
            this.addToSegment(data);
          },
        });
        result.push({
          title: "Add '" + data.query + "' to a product seeds",
          onClick: () => {
            this.addToSeeds(data);
          },
        });
      }
      if (data.queryType == QueryType.PRODUCT && !data.isUnknownAsin) {
        result.push({
          title: "Add '" + data.query + "' to a product segment",
          onClick: () => {
            this.addToSegment(data);
          },
        });
        result.push({
          title: "Add " + data.query + " to a product seeds",
          onClick: () => {
            this.addToSeeds(data);
          },
        });
      }
    }

    if (this.canAccessKeywordRanking()) {
      if (!data.isMultiAsin) {
        result.push({
          title: "Open '" + data.asin + "' in product tracker",
          onClick: () => {
            this.viewProductTracker(data.asin);
          },
        });
      }

      if (data.queryType === QueryType.KEYWORDS) {
        result.push({
          title: "See ranking of keyword '" + data.query + "'",
          onClick: () => {
            this.viewKeywordTracker(data);
          },
        });
        if (!this.isReadOnly) {
          result.push({
            title: "Track keyword '" + data.query + "'",
            onClick: () => {
              this.trackKeyword(data);
            },
          });
        }
      } else if (data.queryType === QueryType.PRODUCT && !data.isUnknownAsin) {
        result.push({
          title: "Open '" + data.query + "' in product tracker",
          onClick: () => {
            this.viewProductTracker(data.query);
          },
        });

        if (!this.isReadOnly) {
          result.push({
            title: "Track product '" + data.query + "'",
            onClick: () => {
              this.trackProduct(data.query);
            },
          });
        }
      }
    }
    return result;
  }

  exportGridCsv(): void {
    this.asinService
      .getCatalog(this.accountMarketplace()!.accountId, this.accountMarketplace()!.marketplace)
      .pipe(
        map((c: Catalog) => c.products),
        untilDestroyed(this),
      )
      .subscribe((products: ProductEx[]) => {
        const asinOrQueryTitles = new Map<string, string>();
        products.forEach((p: ProductEx) => asinOrQueryTitles.set(p.asin!, p.title!));
        const dateRange = this.userSelectionService.getDateRangeStr();
        const fileName = getCsvFileName(
          "traffic_report",
          this.accountMarketplace()!.accountGroupName,
          this.marketplace,
          dateRange,
        );
        const params = {
          fileName,
          columnKeys: this.grid?.api
            ?.getColumns()
            ?.map((c) => c.getColId())
            .filter((c) => c !== IMAGE_COL_ID && c !== ACTIONS_COL_ID),
          processCellCallback: (params: any) => {
            if (params.column.getColId() === "queryTitle") {
              if (params.node.data.queryType === QueryType.PRODUCT) {
                return asinOrQueryTitles.get(params.value); // Get ASIN title
              } else {
                return "";
              }
            }
            if (params.column.getColId() === "asinTitle") return asinOrQueryTitles.get(params.value); // Get asin title

            return params.value;
          },
        };
        for (const key of params.columnKeys ?? []) {
          const colDef = this.grid?.api.getColumnDef(key);
          if (colDef && colDef.headerValueGetter) {
            colDef.headerName = (colDef.headerValueGetter as HeaderValueGetterFunc<any, any>)(
              undefined as unknown as HeaderValueGetterParams<any, any>,
            );
          }
        }
        exportGridCsv(this.grid?.api, params);
      });
  }

  toggleChartDisplay(displayed: boolean): void {
    this.chartDisplayed = displayed;
    this.userSelectionService.setUserChartDisplayedPreference(this.localStorageKey, displayed);
  }

  addToSegment(stat: QueryStats) {
    const modalRef = this.modalService.openModal<AddToSegmentModalData, Targeting[]>(AddToSegmentModalComponent, {
      size: "lg",
      modalTitle:
        stat.queryType == QueryType.KEYWORDS
          ? this.translocoService.translate("add-to-segment-modal.add_search_term_to_segment")
          : this.translocoService.translate("add-to-segment-modal.add_product_to_segment"),
      data: {
        segmentType:
          stat.queryType == QueryType.KEYWORDS ? SegmentConfigType.KeywordSegment : SegmentConfigType.ProductSegment,
        targetingItemValue: stat.query!,
        marketplace: this.accountMarketplace()!.marketplace,
        accountId: this.accountMarketplace()!.accountId,
      },
    });

    modalRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((segmentItems) => {
        if (stat.queryType == QueryType.KEYWORDS) {
          this.modalService.openModal(KeywordSegmentModalComponent, {
            data: {
              accountId: this.accountMarketplace()!.accountId,
              marketplace: this.accountMarketplace()!.marketplace,
              initSegmentItems: segmentItems,
            },
            modalTitle: this.translocoService.translate("keyword-segment-modal.create_keyword_segment"),
          });
        } else {
          this.modalService.openModal<ProductSegmentModalData, SegmentEx>(ProductSegmentModalComponent, {
            modalTitle: `${this.translocoService.translate("common.edit")} ${this.translocoService.translate("product-segment-modal.product_segment")}`,
            data: {
              accountId: this.accountMarketplace()!.accountId,
              marketplace: this.accountMarketplace()!.marketplace,
              initSegmentItems: segmentItems,
            },
          });
        }
      });
  }

  addToSeeds(stat: QueryStats) {
    this.modalService.openModal<AddToSeedsModalData, void>(AddToSeedsModalComponent, {
      modalTitle: this.translocoService.translate("add-to-seeds-modal.add_to_seeds"),
      data: {
        seedType: stat.isAsin ? SeedType.Product : SeedType.Keyword,
        asin: stat.asin!,
        targetingItemValue: stat.query!,
        accountId: this.accountMarketplace()!.accountId,
        marketplace: this.accountMarketplace()!.marketplace,
      },
    });
  }

  viewKeywordTracker(stat: QueryStats) {
    if (this.keywordTrackingConfig!.findIndex((s) => s.searchTerm == stat.query) < 0) {
      this.toasterService.warning(
        `Keyword '${new TruncatePipe().transform(stat.query!, 10)}' is not tracked. Click here to start tracking`,
      );
      return;
    }
    this.router.navigate(["/keyword-center/keyword-tracker", stat.query], {
      queryParamsHandling: "merge",
    });
  }

  viewProductTracker(asin: string) {
    if (!this.catalog?.contains(asin) && this.productTrackingConfig!.findIndex((s) => s.asin == asin) < 0) {
      this.toasterService.warning(`Product '${asin}' is not tracked. Click here to start tracking`);
      return;
    }
    this.router.navigate(["/product-center/product-tracker"], {
      queryParams: { [ProductTrackerPageQueryParams.asin]: asin },
      queryParamsHandling: "merge",
    });
  }

  canAccessKeywordRanking() {
    const accountMarketplace = this.accountMarketplace();
    if (!accountMarketplace) {
      return false;
    }
    return keywordRankingAvailableFor(accountMarketplace.marketplace);
  }

  trackKeyword(stat: QueryStats) {
    if (stat.isAsin) {
      return;
    }

    this.modalService
      .openModal(ConfirmPopupComponent, {
        modalTitle: "Track keyword",
        data: {
          message: `Do you want to track keyword '${stat.query}'?`,
          confirmLabel: "Track",
        },
      })
      .afterClosed()
      .pipe(
        switchMap(() => {
          const conf: KeywordTrackerConfig[] = [
            {
              accountId: this.accountMarketplace()!.accountId,
              marketplace: this.marketplace,
              organizationId: this.accountMarketplace()!.resourceOrganizationId,
              searchTerm: stat.query,
              frequency: KeywordTrackingFrequency.daily,
            },
          ];
          return this.keywordTrackingService.addKeywordTrackerConfig(
            this.accountMarketplace()!.accountId,
            this.marketplace,
            this.accountMarketplace()!.resourceOrganizationId!,
            conf,
          );
        }),
      )
      .subscribe({
        next: (_) => {
          this.toasterService.success("Keyword successfully tracked", "Keyword tracking");
        },
        error: (e) => {
          this.toasterService.success(`Error when setting keyword tracking. ${e}`, "Keyword tracking");
        },
      });
  }

  trackProduct(asin: string) {
    this.modalService
      .openModal(ConfirmPopupComponent, {
        modalTitle: "Track Product",
        data: {
          message: `Do you want to track product '${asin}'?`,
          confirmLabel: "Track",
        },
      })
      .afterClosed()
      .pipe(
        switchMap(() => {
          const conf: ProductTrackerConfig[] = [
            {
              accountId: this.accountMarketplace()!.accountId,
              marketplace: this.marketplace,
              asin: asin,
            },
          ];
          return this.keywordTrackingService.addProductTrackerConfig(
            this.accountMarketplace()!.accountId,
            this.marketplace,
            conf,
          );
        }),
      )
      .subscribe({
        next: (_) => {
          this.toasterService.success("Product successfully tracked", "Product tracking");
        },
        error: (e) => {
          this.toasterService.success(`Error when setting product tracking. ${e}`, "Product tracking");
        },
      });
  }

  private resetQueryStatsData() {
    this.queryStatsData$.next({ data: [], previousData: [] });
    this.loader$.next(true);
  }

  // TODO: this method will be used to infer the queryType as it may be null in DB for the moment
  // this should be cleaned up once stats are reimported
  private inferQueryType(stat: QueryStats): QueryType {
    if (stat.queryType) return stat.queryType;
    if (stat.isAsin) return QueryType.PRODUCT;
    // hacky way error-prone to check if this is a complex targeting expression
    if (stat.query?.includes("=")) return QueryType.COMPLEX_TARGETING_EXPRESSION;
    return QueryType.KEYWORDS;
  }

  private initQueryStatsData(data: AdsStatsWithPreviousPeriod, filter: TrafficStatsFilter) {
    const queryStatsExtraction = this.extractQueryStats(data.data, filter);
    const queryStatsExtractionPreviousPeriod = this.extractQueryStats(data.previousPeriodData, filter);
    this.queryStatsData$.next({
      data: queryStatsExtraction.data,
      previousData: queryStatsExtractionPreviousPeriod.data,
    });
    this.queryStats.set(queryStatsExtraction);
    this.loader$.next(false);
  }

  private extractQueryStats(data: AdStatsEx[], filter: TrafficStatsFilter): QueryStatsExtraction {
    this.loader$.next(true);
    const result: QueryStats[] = [];
    const allData: QueryStats[] = [];
    for (const stats of data) {
      if (!stats.query) continue;
      // filter multi ASIN or not
      let isMultiAsin = false;
      let isUnknownAsin = false;
      if (!Utils.isValidAsin(stats.asin!)) {
        if (stats.asin!.startsWith("Multi-ASIN")) {
          if (!filter.enableMultiAsin) {
            continue;
          }
          isMultiAsin = true;
        } else {
          isUnknownAsin = true;
        }
      }
      const stat: QueryStats = {
        ...stats,
        isAsin: Utils.isValidAsin(stats.query),
        isMultiAsin: isMultiAsin,
        isUnknownAsin: isUnknownAsin,
      };
      stat.queryType = this.inferQueryType(stat);

      // advertised products filter
      let queryMatch = false;
      if (
        !filter.advertisedProducts.every((filter) =>
          filter.exclude ? !filter.filterValue.includes(stat.asin!) : filter.filterValue.includes(stat.asin!),
        )
      ) {
        queryMatch = false;
      } else if (filter.keywordSegments.length == 0 && filter.productSegments.length == 0) {
        queryMatch = true;
      } else if (stat.queryType == QueryType.PRODUCT) {
        queryMatch =
          filter.productSegments.length > 0 &&
          filter.productSegments.every((filter) =>
            filter.exclude
              ? !filter.filterValue.some((s) => s.matchQuery(stat.query!))
              : filter.filterValue.some((s) => s.matchQuery(stat.query!)),
          );
      } else if (stat.queryType == QueryType.KEYWORDS) {
        queryMatch =
          filter.keywordSegments.length > 0 &&
          filter.keywordSegments.every((filter) =>
            filter.exclude
              ? !filter.filterValue.some((s) => s.matchQuery(stat.query!))
              : filter.filterValue.some((s) => s.matchQuery(stat.query!)),
          );
      } else {
        // segment cannot match complex targeting expression, except if they exclude them
        queryMatch =
          filter.keywordSegments.every((filter) => filter.exclude) &&
          filter.productSegments.every((filter) => filter.exclude);
      }
      if (queryMatch) {
        stat.segmentBucket = "segment";
        result.push(stat);
      } else {
        stat.segmentBucket = "nonSegment";
      }
      allData.push(stat);
    }
    return {
      data: result,
      allData,
    };
  }

  private initAggregatedData(data: QueryStats[], previousData: QueryStats[]) {
    this.aggregatedData.set(this.sumData(data));
    this.previousAggregatedData.set(this.sumData(previousData));
  }

  private updateTableData(data: QueryStats[], previousData: QueryStats[] = []) {
    this.gridData.set(data);
    this.previousGridData.clear();
    for (const stat of previousData) {
      this.previousGridData.set(`${stat.query}_${stat.asin}`, stat);
    }
  }

  private getTopQueries(stats: QueryStats[], metric: Metric<QueryStats>): Observable<QueryStats[]> {
    return defer(async () => {
      await new Promise((resolve) => setTimeout(resolve, 0));
      // for ACOS metric, sort in the reverse order https://github.com/m19-dev/main-repo/issues/1160
      const order = metric == ACOS ? -1 : 1;
      const aggregation = new Map<string, QueryStats[]>();
      for (const stat of stats) {
        if (!aggregation.has(stat.query!)) {
          aggregation.set(stat.query!, []);
        }
        aggregation.set(stat.query!, [...aggregation.get(stat.query!)!, stat]);
      }
      const result = [...aggregation.entries()]
        .map(([_, v]) => this.sumData(v))
        .filter((d) => {
          if (metric == ACOS) {
            const value = metric.value(d)!;
            return !isNaN(value) && isFinite(value);
          }
          return true;
        })
        .sort((a, b) => order * metric.compare(a, b))
        .slice(0, 5);
      return result;
    });
  }

  protected readonly STATUS_BAR = STATUS_BAR;
}
